import React from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Divider,
} from '@mui/material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Line, Doughnut } from 'react-chartjs-2';
import {
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const EmployeeAnalytics = ({ data, meta }) => {
  if (!data) return null;

  // Employee by Department Chart
  const departmentData = {
    labels: data.byDepartment?.map(item => item._id) || [],
    datasets: [
      {
        label: 'Employees',
        data: data.byDepartment?.map(item => item.count) || [],
        backgroundColor: [
          '#1976d2',
          '#2e7d32',
          '#f57c00',
          '#9c27b0',
          '#d32f2f',
          '#7b1fa2',
          '#0288d1',
          '#388e3c',
          '#f57f17',
          '#6a1b9a'
        ],
        borderWidth: 1,
      },
    ],
  };

  // Employee Status Distribution
  const statusData = {
    labels: data.byStatus?.map(item => item._id) || [],
    datasets: [
      {
        data: data.byStatus?.map(item => item.count) || [],
        backgroundColor: ['#4caf50', '#2196f3', '#ff9800', '#f44336'],
        hoverBackgroundColor: ['#66bb6a', '#64b5f6', '#ffb74d', '#e57373'],
      }
    ]
  };

  // Joining Trend Chart
  const joiningTrendData = {
    labels: data.joiningTrend?.map(item => item.month) || [],
    datasets: [
      {
        label: 'New Employees',
        data: data.joiningTrend?.map(item => item.count) || [],
        borderColor: '#1976d2',
        backgroundColor: 'rgba(25, 118, 210, 0.1)',
        borderWidth: 2,
        fill: true,
        tension: 0.4,
        pointBackgroundColor: '#1976d2',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 4,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  };

  const pieOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'bottom',
      },
    },
  };

  const lineOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
    interaction: {
      mode: 'index',
      intersect: false,
    },
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
        Employee Analytics Overview
      </Typography>

      {/* Key Metrics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#e3f2fd', height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <PeopleIcon sx={{ color: '#1976d2', mr: 1 }} />
                <Typography variant="h4" sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                  {data.total || 0}
                </Typography>
              </Box>
              <Typography variant="body2">Total Employees</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#e8f5e8', height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <PeopleIcon sx={{ color: '#2e7d32', mr: 1 }} />
                <Typography variant="h4" sx={{ color: '#2e7d32', fontWeight: 'bold' }}>
                  {data.active || 0}
                </Typography>
              </Box>
              <Typography variant="body2">Active Employees</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#fff3e0', height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <TrendingUpIcon sx={{ color: '#f57c00', mr: 1 }} />
                <Typography variant="h4" sx={{ color: '#f57c00', fontWeight: 'bold' }}>
                  {data.newThisMonth || 0}
                </Typography>
              </Box>
              <Typography variant="body2">New This Month</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#f3e5f5', height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <TrendingUpIcon sx={{ color: '#9c27b0', mr: 1 }} />
                <Typography variant="h4" sx={{ color: '#9c27b0', fontWeight: 'bold' }}>
                  {data.newThisYear || 0}
                </Typography>
              </Box>
              <Typography variant="body2">New This Year</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts Section */}
      <Grid container spacing={3}>
        {/* Department Distribution */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <BusinessIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Employees by Department
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ height: 300 }}>
                <Bar
                  data={departmentData}
                  options={{
                    ...chartOptions,
                    plugins: {
                      ...chartOptions.plugins,
                      legend: {
                        display: false,
                      },
                    },
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Employee Status Distribution */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <PeopleIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Employee Status Distribution
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ height: 300, display: 'flex', justifyContent: 'center' }}>
                <Doughnut data={statusData} options={pieOptions} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Joining Trend */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <TrendingUpIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Employee Joining Trend (Last 12 Months)
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ height: 300 }}>
                <Line
                  data={joiningTrendData}
                  options={lineOptions}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Additional Insights */}
      {data.byDepartment && data.byDepartment.length > 0 && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Department Insights
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              {data.byDepartment.map((dept, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Box sx={{
                    p: 2,
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                    textAlign: 'center'
                  }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {dept.count}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {dept._id}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default EmployeeAnalytics;
